import { effect, inject, Injectable, signal } from '@angular/core';
import { PageViewedEvent, PageViewedName, PageViewedOrigin } from './events/page-viewed-event';
import { AnalyticsManager } from './analytics-manager';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { UserActionEvent, UserActionName } from './events/user-event';
import { SearchPerformedEvent, SearchPerformedOrigin } from './events/search-performed-event';
import { CookiesFacade } from '../state/dialogs/cookies/cookies.facade';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ContentViewedAccessMode,
  ContentViewedAccessType,
  ContentViewedEvent,
  ContentViewedFormat,
} from './events/content-viewed-event';
import { Record } from '../../models/record';
import { formatsMapper } from '../utils/formats-mapper';

export interface ContentViewedEventOptions {
  learningExperienceId?: string;
  isRssItem?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly analyticsManager = inject(AnalyticsManager);
  private readonly router = inject(Router);
  private readonly cookiesFacade = inject(CookiesFacade);

  private readonly optionalCookies = toSignal(this.cookiesFacade.optionalCookies$);
  private readonly currentPage = signal<PageViewedName | undefined>(undefined);
  private readonly previousPage = signal<PageViewedName | undefined>(undefined);

  private readonly storage: { searchPerformedOrigin: SearchPerformedOrigin } = {
    searchPerformedOrigin: 'none',
  };

  get searchPerformedOrigin() {
    const origin = this.storage.searchPerformedOrigin;
    this.storage.searchPerformedOrigin = 'none';
    return origin;
  }

  set searchPerformedOrigin(origin: SearchPerformedOrigin) {
    this.storage.searchPerformedOrigin = origin;
  }

  constructor() {
    effect(
      () => {
        this.optionalCookies().forEach((cookie) => {
          if (cookie.accepted) {
            this.analyticsManager.activate(cookie.id);
          } else {
            this.analyticsManager.deactivate(cookie.id);
          }
        });
      },
      { allowSignalWrites: true }
    );
  }

  sendCheckoutEvent(isSuccessful: boolean): void {
    this.analyticsManager.sendEvent(
      new UserActionEvent({
        page_name: this.currentPage(),
        action_name: 'checkout',
        is_successful: isSuccessful,
      })
    );
  }

  sendContentViewedEvent(record: Record, accessType: ContentViewedAccessType, accessMode: ContentViewedAccessMode, options?: ContentViewedEventOptions): void {
     function getFormat(formats: string[], leId?: string): ContentViewedFormat {
      if (leId) {
        return 'experience';
      }

      const format = formats.find((f) => formatsMapper[f]);

      if (format) {
        return formatsMapper[format];
      }

      return 'other';
    }

    const { learningExperienceId, isRssItem } = options ?? {};

    const event = new ContentViewedEvent({
      resource_id: record.id,
      format: getFormat([...record.resourceTypes, ...record.formats], learningExperienceId ?? record.learningExperienceId),
      access_mode: accessMode,
      access_type: accessType,
      is_rss_item: isRssItem,
    });

    this.analyticsManager.sendEvent(event);
  }

  sendFollowAuthorEvent(isSuccessful: boolean): void {
    this.analyticsManager.sendEvent(
      new UserActionEvent({
        page_name: this.currentPage(),
        action_name: 'follow_author',
        is_successful: isSuccessful,
      })
    );
  }

  sendFollowListEvent(isSuccessful: boolean): void {
    this.analyticsManager.sendEvent(
      new UserActionEvent({
        page_name: this.currentPage(),
        action_name: 'follow_list',
        is_successful: isSuccessful,
      })
    );
  }

  sendHoldEvent(isSuccessful: boolean): void {
    this.analyticsManager.sendEvent(
      new UserActionEvent({
        page_name: this.currentPage(),
        action_name: 'hold',
        is_successful: isSuccessful,
      })
    );
  }

  sendListCreatedEvent(isSuccessful: boolean): void {
    this.analyticsManager.sendEvent(
      new UserActionEvent({
        page_name: this.currentPage(),
        action_name: 'create_list',
        is_successful: isSuccessful,
      })
    );
  }

  sendUserActionEvent(actionName: UserActionName, isSuccessful: boolean): void {
    this.analyticsManager.sendEvent(
      new UserActionEvent({
        page_name: this.currentPage(),
        action_name: actionName,
        is_successful: isSuccessful,
      })
    );
  }

  sendPageViewEvent(viewOrigin: PageViewedOrigin): void {
    const route = this.getChildRoute(this.router.routerState.snapshot.root);
    const currentPage: PageViewedName = route.data['name'];

    this.previousPage.set(this.currentPage());
    this.currentPage.set(currentPage);

    const listId = currentPage === 'LDP' ? route.paramMap.get('slug')?.split('-').at(-1) : undefined;
    const recordId = currentPage === 'TDP' ? route.paramMap.get('slug')?.split('-').at(-1) : undefined;

    const event = new PageViewedEvent({
      previous_section: this.previousPage(),
      page_name: currentPage,
      view_origin: viewOrigin,
      list_id: listId,
      record_id: recordId,
    });

    this.analyticsManager.sendEvent(event);
  }

  sendRenewalEvent(isSuccessful: boolean): void {
    this.analyticsManager.sendEvent(
      new UserActionEvent({
        page_name: this.currentPage(),
        action_name: 'renewal',
        is_successful: isSuccessful,
      })
    );
  }

  sendSearchPerformedEvent(facets: string[], hasResults: boolean, origin: SearchPerformedOrigin): void {
    this.analyticsManager.sendEvent(
      new SearchPerformedEvent({
        facets,
        has_results: hasResults,
        search_origin: origin,
      })
    );
  }

  private getChildRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let childRoute = route.firstChild;

    while (childRoute && childRoute.firstChild) {
      childRoute = childRoute.firstChild;
    }

    return childRoute;
  }
}
